import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { 
  Loader2,
  Search,
  Star,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Filter,
  ArrowRight,
  Pill,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stockStatusColors = {
  available: 'bg-success/10 text-success border-success/20',
  low: 'bg-warning/10 text-warning border-warning/20',
  out_of_stock: 'bg-destructive/10 text-destructive border-destructive/20',
};

const stockStatusLabels = {
  available: 'Available',
  low: 'Low Stock',
  out_of_stock: 'Out of Stock',
};

const stockStatusIcons = {
  available: CheckCircle,
  low: AlertTriangle,
  out_of_stock: XCircle,
};

const categoryLabels = {
  acne: 'Acne',
  pigmentation: 'Pigmentation',
  eczema: 'Eczema',
  psoriasis: 'Psoriasis',
  fungal: 'Fungal',
  bacterial: 'Bacterial',
  viral: 'Viral',
  antihistamine: 'Antihistamine',
  moisturizer: 'Moisturizer',
  sunscreen: 'Sunscreen',
  'anti-aging': 'Anti-Aging',
  hair_loss: 'Hair Loss',
  other: 'Other',
};

const formLabels = {
  tablet: 'Tablet',
  capsule: 'Capsule',
  cream: 'Cream',
  ointment: 'Ointment',
  gel: 'Gel',
  lotion: 'Lotion',
  solution: 'Solution',
  injection: 'Injection',
  powder: 'Powder',
  shampoo: 'Shampoo',
  serum: 'Serum',
  drops: 'Drops',
  spray: 'Spray',
  other: 'Other',
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function DoctorMedicineSearchPage() {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  
  // Detail modal
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchMedicines = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      params.append('is_active', 'true');
      
      const { data } = await axios.get(`${API_URL}/api/medicines?${params}`, { withCredentials: true });
      setMedicines(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to fetch medicines');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/medicines/categories`, { withCredentials: true });
      setCategories(data.categories);
    } catch (error) {
      console.error('Failed to fetch categories', error);
    }
  }, []);

  useEffect(() => {
    fetchMedicines();
    fetchCategories();
  }, [fetchMedicines, fetchCategories]);

  const openMedicineDetail = async (medicine) => {
    setLoadingDetail(true);
    setIsDetailOpen(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/medicines/${medicine.id}`, { withCredentials: true });
      setSelectedMedicine(data);
    } catch (error) {
      toast.error('Failed to load medicine details');
      setIsDetailOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const StockIcon = ({ status }) => {
    const Icon = stockStatusIcons[status] || CheckCircle;
    return <Icon className={`w-4 h-4 ${status === 'available' ? 'text-success' : status === 'low' ? 'text-warning' : 'text-destructive'}`} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="doctor-medicine-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="doctor-medicine-search-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">
          Medicine Search
        </h1>
        <p className="text-muted-foreground mt-1">
          Search medicines, check availability, and view substitutes for prescriptions
        </p>
      </div>

      {/* Search & Filters */}
      <Card className="border-border shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search by medicine name, generic name, or brand..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 text-lg rounded-xl"
                data-testid="doctor-search-medicines-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48 h-12 rounded-xl" data-testid="doctor-category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{categoryLabels[cat] || cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {medicines.length === 0 ? (
          <Card className="col-span-full border-border shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Pill className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                {searchTerm ? 'No medicines found matching your search' : 'Search for medicines to view stock and substitutes'}
              </p>
            </CardContent>
          </Card>
        ) : (
          medicines.map((medicine) => (
            <Card 
              key={medicine.id} 
              className="border-border shadow-sm card-interactive cursor-pointer"
              onClick={() => openMedicineDetail(medicine)}
              data-testid={`doctor-medicine-card-${medicine.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {medicine.is_preferred && (
                      <Star className="w-4 h-4 text-warning fill-warning" />
                    )}
                    <Badge variant="outline" className={stockStatusColors[medicine.stock_status]}>
                      <StockIcon status={medicine.stock_status} />
                      <span className="ml-1">{stockStatusLabels[medicine.stock_status]}</span>
                    </Badge>
                  </div>
                  <Badge variant="outline" className="capitalize text-xs">
                    {formLabels[medicine.form] || medicine.form}
                  </Badge>
                </div>
                
                <h3 className="font-heading font-medium text-lg mb-1">{medicine.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  {medicine.generic_name} • {medicine.strength}
                </p>
                <p className="text-sm text-muted-foreground mb-3">
                  <span className="font-medium text-foreground">{medicine.brand}</span>
                </p>
                
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <Badge variant="outline" className="capitalize">
                    {categoryLabels[medicine.category] || medicine.category}
                  </Badge>
                  <span className="font-heading font-semibold text-primary">₹{medicine.price.toFixed(2)}</span>
                </div>
                
                <div className="flex items-center justify-center mt-3 pt-3 border-t border-border text-sm text-primary">
                  <Info className="w-4 h-4 mr-1" />
                  View details & substitutes
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Medicine Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="medicine-detail-modal">
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : selectedMedicine ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  {selectedMedicine.is_preferred && (
                    <Star className="w-5 h-5 text-warning fill-warning" />
                  )}
                  <Badge variant="outline" className={stockStatusColors[selectedMedicine.stock_status]}>
                    <StockIcon status={selectedMedicine.stock_status} />
                    <span className="ml-1">{stockStatusLabels[selectedMedicine.stock_status]}</span>
                  </Badge>
                </div>
                <DialogTitle className="font-heading text-xl">{selectedMedicine.name}</DialogTitle>
                <DialogDescription>
                  {selectedMedicine.generic_name} • {selectedMedicine.strength} • {formLabels[selectedMedicine.form]}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Brand</p>
                    <p className="font-medium">{selectedMedicine.brand}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Category</p>
                    <p className="font-medium capitalize">{categoryLabels[selectedMedicine.category]}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Price</p>
                    <p className="font-heading font-semibold text-primary text-lg">₹{selectedMedicine.price.toFixed(2)}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Status</p>
                    <p className="font-medium">{selectedMedicine.is_active ? 'Active' : 'Inactive'}</p>
                  </div>
                </div>

                {/* Substitutes Section */}
                <div className="pt-4 border-t border-border">
                  <h4 className="font-heading font-medium mb-3 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-primary" />
                    Substitute Medicines ({selectedMedicine.substitutes?.length || 0})
                  </h4>
                  
                  {selectedMedicine.substitutes && selectedMedicine.substitutes.length > 0 ? (
                    <div className="space-y-2">
                      {selectedMedicine.substitutes.map((sub) => (
                        <div 
                          key={sub.id} 
                          className="p-3 rounded-xl border border-border flex items-center justify-between"
                          data-testid={`substitute-${sub.id}`}
                        >
                          <div>
                            <p className="font-medium">{sub.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {sub.generic_name} • {sub.strength} • {sub.brand}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-medium">₹{sub.price.toFixed(2)}</span>
                            <Badge variant="outline" className={stockStatusColors[sub.stock_status]}>
                              <StockIcon status={sub.stock_status} />
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-xl">
                      No substitutes available for this medicine
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
